import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  User,
  Phone,
  MapPin,
  Calendar,
  Activity,
  Loader2,
  Briefcase,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  Building,
  Mail,
  MoreVertical,
  Trash2,
  ShieldCheck,
  TrendingUp,
  CreditCard
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewCustomer.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import NotFound from '../Error/NotFound';

import CustomerDrawer from './CustomerDrawer';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ViewCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customers/${id}`);
      if (response.data.success) {
        setCustomer(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const handleDelete = () => {
     MySwal.fire({
      title: 'Delete Customer?',
      text: `Are you sure you want to delete ${customer.customer_name}? This action will permanently remove the record.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/customers/${customer.id}`);
          if (response.data.success) {
            toast.success('Customer deleted successfully');
            navigate('/customers');
          }
        } catch (error) {
          toast.error(error.response?.data?.message || 'Error deleting customer');
        }
      }
    });
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = [
      '#f97316', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', 
      '#ec4899', '#f59e0b', '#6366f1', '#14b8a6', '#f43f5e'
    ];
    let hash = 0;
    const str = name || 'Customer';
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className={`${styles['view-customer-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Gathering Profile Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return <NotFound />;
  }


  return (
    <div className={`${styles['view-customer-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Customer Details</h1>
              <p>View comprehensive profile, billing history, and relationship status</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/customers')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Edit Profile</span>
              </button>
              <button className={styles['btn-secondary']} onClick={handleDelete} style={{ color: '#ef4444', borderColor: '#fee2e2' }}>
                <Trash2 size={18} />
              </button>
            </div>
          </div>

          {/* Customer Dashboard Header Card */}
          <div className={styles['customer-dashboard-header']}>
            <div className={styles['header-main']}>
              <div 
                className={styles['avatar-large']}
                style={{ backgroundColor: getAvatarColor(customer.customer_name) }}
              >
                {getInitials(customer.customer_name)}
              </div>
              <div className={styles['customer-basic-info']}>
                <div className={styles['title-flex']}>
                  <h1>{customer.customer_name}</h1>
                  <span className={`${styles['status-badge']} ${styles['verified']}`}>
                    <ShieldCheck size={14} /> ACTIVE CUSTOMER
                  </span>
                </div>
                <div className={styles['customer-meta']}>
                  <div className={styles['meta-item']}>
                    <User size={16} />
                    <span>Onboarded By: <strong>{customer.creator?.name || 'System Admin'}</strong></span>
                  </div>
                  <div className={styles['meta-item']}>
                    <Calendar size={16} />
                    <span>Member Since: <strong>{new Date(customer.created_at).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-blue']}`}><Briefcase size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Active Projects</label>
                <h2>03</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-green']}`}><TrendingUp size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Total Billing</label>
                <h2>₹85.40L</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-orange']}`}><AlertCircle size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Outstanding</label>
                <h2 style={{ color: '#f97316' }}>₹12.25L</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-slate']}`}><CreditCard size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Collections</label>
                <h2>₹73.15L</h2>
              </div>
            </div>
          </div>

          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Primary Information */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Building size={20} /></div>
                  <h3>Primary Information</h3>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Registered Name</span>
                      <span className={styles['d-value']}>{customer.customer_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>Client Code</span>
                      <span className={styles['d-value']}>CUST-{customer.id}</span>
                    </div>
                  </div>
                  <div className={styles['detail-row']}>
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>GST Number</span>
                      <span className={styles['d-value']}>{customer.gst || 'Not Provided'}</span>
                    </div>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Official Address</span>
                    <div className={styles['address-box']}>
                      <MapPin size={16} style={{ marginTop: '2px' }} />
                      <span>{customer.address || 'No registered address available for this client.'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Relationship Activity */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={18} /></div>
                  <h3>Relationship History</h3>
                </div>
                <div className={styles['empty-activity']}>
                    <Activity size={32} />
                    <p>Financial transaction and project history will be summarized here once integrated.</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Contact Hub */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Phone size={20} /></div>
                  <h3>Contact Hub</h3>
                </div>
                <div className={styles['contact-list']}>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><Phone size={16} /></div>
                    <div className={styles['p-info']}>
                       <label>PRIMARY PHONE</label>
                       <span>{customer.mobile}</span>
                    </div>
                  </div>
                  <div className={styles['contact-pill']}>
                    <div className={styles['p-icon']}><Mail size={16} /></div>
                    <div className={styles['p-info']}>
                       <label>EMAIL ADDRESS</label>
                       <span>{customer.email || 'No email registered'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Registry Metadata */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><ShieldCheck size={20} /></div>
                  <h3>Registry Metadata</h3>
                </div>
                <div className={styles['meta-list']}>
                  <div className={styles['meta-row']}>
                    <span className={styles['m-label']}>Registered By</span>
                    <span className={styles['m-value']}>{customer.creator?.name || 'System Admin'}</span>
                  </div>
                  <div className={styles['meta-row']}>
                    <span className={styles['m-label']}>Creation Date</span>
                    <span className={styles['m-value']}>{new Date(customer.created_at).toLocaleDateString('en-IN')}</span>
                  </div>
                  <div className={styles['meta-row']}>
                    <span className={styles['m-label']}>Last Updated</span>
                    <span className={styles['m-value']}>{new Date(customer.updated_at).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <CustomerDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={fetchCustomer}
        customer={customer}
      />
    </div>
  );
};

export default ViewCustomer;
